import { WORKFLOW_ACTIONS } from 'modules/workflows/constants';

const isChannelIdValid = (channelId) => typeof channelId === 'number';
const isMessageTypeIdValid = (messageTypeId) => typeof messageTypeId === 'number';

/**
 * Use multivalue if:
 *  1. `multiValue` is an array
 *  2. `multiValue` has at least one item OR `singleValue` does not exist
 *
 * This makes sure that the singular value will be shown if the singleValue
 * exists and the multiValue is empty.
 */
const isMultiValueShown = (singleValue, multiValue) =>
  Array.isArray(multiValue) && (!!multiValue.length || !singleValue);

const createSegments = ({
  singleValue,
  multiValue,
  singularText,
  pluralText,
  subscribed,
  resource,
}) => {
  const firstLine = [subscribed ? 'Subscribe to ' : 'Unsubscribe from '];
  const secondLine = [];
  const segments = [];

  if (isMultiValueShown(singleValue, multiValue)) {
    const numItems = multiValue.length;
    firstLine.push(`${numItems} ${numItems === 1 ? singularText : pluralText}`);
  } else {
    firstLine.push(singularText);
    const resourceName = resource?.name;
    if (resourceName) {
      firstLine.push(':');
      secondLine.push(resourceName);
    }
  }

  segments.push([{ text: firstLine.join('') }]);
  if (secondLine.length) {
    segments.push([{ text: secondLine.join('') }]);
  }

  return segments;
};

const getDescription = (nodeData = {}) => {
  const { nodeClass, subscribed, channelId, channelIds, messageTypeId, messageTypeIds, resource } =
    nodeData;
  switch (nodeClass) {
    case WORKFLOW_ACTIONS.MODIFY_CHANNEL_SUBSCRIPTION:
      return createSegments({
        singleValue: channelId,
        multiValue: channelIds,
        singularText: 'channel',
        pluralText: 'channels',
        subscribed,
        resource,
      });
    case WORKFLOW_ACTIONS.MODIFY_MESSAGE_TYPE:
      return createSegments({
        singleValue: messageTypeId,
        multiValue: messageTypeIds,
        singularText: 'type',
        pluralText: 'types',
        subscribed,
        resource,
      });
    default:
      return [];
  }
};

/*
  Modify Subscription node can double as two different nodes (for backwards compatability).
  Since the node type can be hot-switched, we need to look at the current nodeClass to determine
  setup status. Just checking the initially fetched node type is not enough.
*/

const modifySubscription = {
  isSetup: ({ nodeClass, channelId, messageTypeId }) /*: boolean */ => {
    // if it's a MODIFY_CHANNEL_SUBSCRIPTION node, we need to check for a valid channel id
    // if it's a MODIFY_MESSAGE_TYPE, we need to check for a valid message type id

    switch (nodeClass) {
      case WORKFLOW_ACTIONS.MODIFY_CHANNEL_SUBSCRIPTION:
        return isChannelIdValid(channelId);
      case WORKFLOW_ACTIONS.MODIFY_MESSAGE_TYPE:
        return isMessageTypeIdValid(messageTypeId);
      default:
        return false;
    }
  },

  getDescription: (nodeData) => ({
    nodeDescBottom: {
      isTruncated: true,
      segments: getDescription(nodeData),
    },
  }),
  isMultiValueShown,
};

export default { modifySubscription };
